import React, { useState, useEffect, useMemo } from "react";
import { Sparkles, AlertTriangle } from "lucide-react";
import { AppModal, AppButton } from "../ui";

/**
 * Map a Meta lead form's questions onto Enquiry fields.
 *
 * Meta's *standard* questions (Email, Full name, Phone) are recognised by the
 * adapter's built-in table and need no help. Everything else is a **custom
 * question the institute wrote themselves** — "Which branch?", "Preferred
 * timing?" — whose key is per-form and unguessable, so before this screen those
 * answers were collected by Meta, delivered to us, and then dropped.
 *
 * The screen is therefore built around one question: *which of these still needs
 * a decision?* Standard ones default to "Automatic" and stay quiet; custom ones
 * are flagged until they are either mapped or explicitly kept as a note.
 */

/** Friendly names for the DTO targets the backend accepts. */
const TARGET_LABELS = {
  fullName: "Full name",
  firstName: "First name",
  lastName: "Last name",
  email: "Email",
  mobile: "Phone number",
  city: "City",
  region: "State / region",
  postalCode: "Postal code",
  country: "Country",
  address: "Address",
  companyName: "Company",
  jobTitle: "Job title",
  courseInterest: "Course interested in",
  comments: "Notes",
};

/**
 * Meta question types the adapter already understands without a mapping.
 * Anything outside this set is custom and worth drawing attention to.
 */
const STANDARD_TYPES = new Set([
  "FULL_NAME", "FIRST_NAME", "LAST_NAME", "EMAIL", "WORK_EMAIL",
  "PHONE", "PHONE_NUMBER", "WORK_PHONE_NUMBER", "CITY", "STATE",
  "PROVINCE", "ZIP_CODE", "POST_CODE", "COUNTRY", "STREET_ADDRESS",
  "COMPANY_NAME", "JOB_TITLE",
]);

const isStandard = (q) => STANDARD_TYPES.has((q.type || "").toUpperCase());

const MetaLeadFormMappingModal = ({ show, form, targets, saving, onHide, onSave }) => {
  const [mapping, setMapping] = useState({});

  // Re-seed whenever a different form is opened. Without the reset, closing one
  // form and opening another shows the first form's choices against the second
  // form's questions — which then saves them.
  useEffect(() => {
    setMapping({ ...(form?.fieldMapping || {}) });
  }, [form]);

  const questions = form?.questions || [];

  const customUnmapped = useMemo(
    () => questions.filter((q) => !isStandard(q) && !mapping[q.key]).length,
    [questions, mapping]
  );

  const setTarget = (key, value) =>
    setMapping((prev) => {
      const next = { ...prev };
      if (!value) delete next[key];
      else next[key] = value;
      return next;
    });

  // A field can only take one question — offering a target that is already
  // claimed just produces a server-side rejection after the click.
  const claimed = new Set(
    Object.entries(mapping)
      .filter(([, v]) => v && !v.startsWith("custom:"))
      .map(([, v]) => v)
  );

  const options = targets?.length ? targets : Object.keys(TARGET_LABELS);

  return (
    <AppModal
      show={show}
      onHide={onHide}
      size="lg"
      title={`Field mapping — ${form?.name || "Lead form"}`}
      footer={
        <>
          <AppButton variant="outline" onClick={onHide} disabled={saving}>
            Cancel
          </AppButton>
          <AppButton onClick={() => onSave(mapping)} disabled={saving}>
            {saving ? "Saving…" : "Save mapping"}
          </AppButton>
        </>
      }
    >
      <p className="mlf-map__intro">
        Choose where each answer should land on the enquiry. Standard questions are
        handled automatically — you only need to map the ones you wrote yourself.
      </p>

      {customUnmapped > 0 && (
        <div className="mlf-map__warn">
          <AlertTriangle size={14} />
          <span>
            {customUnmapped} custom {customUnmapped === 1 ? "question is" : "questions are"} not
            mapped. {customUnmapped === 1 ? "Its answer" : "Their answers"} will be kept as a note
            on the enquiry rather than filling a field.
          </span>
        </div>
      )}

      {questions.length === 0 ? (
        <div className="mlf-map__empty">
          This form's questions have not been synced yet. Click <strong>Sync Pages</strong> and
          reopen.
        </div>
      ) : (
        <div className="mlf-map__list">
          {questions.map((q) => {
            const std = isStandard(q);
            const value = mapping[q.key] || "";
            return (
              <div className={`mlf-map__row${std ? " mlf-map__row--std" : ""}`} key={q.key}>
                <div className="mlf-map__q">
                  <div className="mlf-map__label">
                    {q.label || q.key}
                    {std && (
                      <span className="mlf-map__badge">
                        <Sparkles size={11} /> Automatic
                      </span>
                    )}
                  </div>
                  <div className="mlf-map__key">{q.key}</div>
                  {q.options?.length > 0 && (
                    <div className="mlf-map__opts">Choices: {q.options.join(" · ")}</div>
                  )}
                </div>

                <select
                  className="mlf-map__select"
                  value={value}
                  onChange={(e) => setTarget(q.key, e.target.value)}
                >
                  <option value="">{std ? "Automatic" : "Keep as a note"}</option>
                  {options.map((t) => (
                    <option
                      key={t}
                      value={t}
                      // Free if unclaimed, or claimed by this very question.
                      disabled={claimed.has(t) && value !== t}
                    >
                      {TARGET_LABELS[t] || t}
                    </option>
                  ))}
                  <option value={`custom:${q.label || q.key}`}>
                    Keep as a note (labelled)
                  </option>
                </select>
              </div>
            );
          })}
        </div>
      )}
    </AppModal>
  );
};

export default MetaLeadFormMappingModal;
